import React, { JSX, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cell, IdType } from 'react-table';
import { Identifiable } from '@custis/common';
import { isEqual } from 'lodash';
import { TableRenderComponents, TTableColumn } from '../../../models';
import { Form } from 'antd';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import s from './EditableRow.module.less';
import { getCellField, getFieldProps } from '../fields';
import { formatCellByColumnType } from '../../../utils';
import { CellAlign } from '../../cell-align/CellAlign';
import { KEYBOARD_KEYS } from '../../../constants';
import { RowValidationPopoverData } from './models';
import { getInputRulesFromColumnItem, useSubmitValidate } from './rowHelpers';
import classNames from 'classnames';
import { RowValidationPopover } from './RowValidationPopover';
import { observer } from 'mobx-react';

type ReactHTML = JSX.IntrinsicElements;

interface Props<T extends Identifiable, TFilter, TSorting> {
  cells: Array<Cell<T>>;
  columns: TTableColumn<T, TFilter, TSorting>[];
  data?: Partial<T>;
  originalRow?: Partial<T>;
  cellClassName?: string;
  visible?: boolean;
  hiddenColumns: IdType<T>[];

  onEdit?(row: T): Promise<void>;

  onFormChange?(row: Partial<T>): void;

  onHide(): void;
}

export const EditRow = observer(
  <T extends Identifiable, TFilter, TSorting>({
    cells,
    columns,
    onEdit,
    onHide,
    data,
    cellClassName,
    onFormChange,
    originalRow,
    visible,
    hiddenColumns,
  }: Props<T, TFilter, TSorting>) => {
    const [form] = Form.useForm<T>();
    const ref = useRef<HTMLDivElement>(null);
    const errorRef = useRef<HTMLDivElement>(null);
    const [popoverData, setPopoverData] = useState<RowValidationPopoverData>(null);
    const [clickableElements, setClickableElements] = useState<Element[]>([]);
    const visibleColumns = useMemo(
      () => columns.filter((item) => item.id && !hiddenColumns.includes(item.id)),
      [columns, hiddenColumns],
    );

    const handleEdit = useCallback(
      (row: T) => {
        if (onEdit && !isEqual(row, originalRow)) {
          void onEdit({
            ...data,
            ...row,
          }).finally(() => {
            form.resetFields();
            onHide();
          });
        } else {
          onHide();
        }
      },
      [data, originalRow],
    );

    const { onSubmit } = useSubmitValidate({ columns, setPopoverData, form, handleSave: handleEdit });

    const getFieldTypeByColumnId = useCallback(
      (id: string) => {
        return columns.find((item) => item.id === id)?.fieldType;
      },
      [columns],
    );

    useEffect(() => {
      columns.forEach((item) => {
        if (item.fieldType && ['select', 'multi-select'].includes(item.fieldType)) {
          item?.store?.reset();
        }
      });
    }, []);

    useEffect(() => {
      if (data) {
        const fields = Object.entries(data).map(([key, value]) => {
          const fieldType = getFieldTypeByColumnId(key);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const floatValue = parseFloat(value);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const finalValue =
            fieldType && ['number', 'integer'].includes(fieldType)
              ? isNaN(floatValue)
                ? undefined
                : floatValue
              : value;
          return {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            name: key as any,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            value: finalValue,
          };
        });
        form.setFields(fields);
      }
    }, [data]);

    useEffect(() => {
      const refElement = ref.current;
      if (refElement && !clickableElements.includes(refElement)) {
        setClickableElements((prevState) => [...prevState, refElement]);
      }
    }, [columns, ref.current]);

    useEffect(() => {
      if (popoverData) {
        const refElement = errorRef.current;
        if (refElement && !clickableElements.includes(refElement)) {
          setClickableElements((prevState) => [...prevState, refElement]);
        }
      }
    }, [popoverData]);

    const onPressEnter = useCallback(
      (e: KeyboardEvent) => {
        e.stopImmediatePropagation();
        if (e.key === KEYBOARD_KEYS.ENTER && visible) {
          onSubmit();
        }
      },
      [visible],
    );

    useEffect(() => {
      window.addEventListener('keydown', onPressEnter);

      return () => {
        window.removeEventListener('keydown', onPressEnter);
      };
    }, []);

    const onClickRow = useCallback(() => {
      columns.forEach((item) => {
        if (item.fieldType && ['select', 'multi-select'].includes(item.fieldType)) {
          const element = document.querySelector(`.${item.id as string}-select`);
          if (element) {
            setClickableElements((prevState) => [...prevState, element]);
          }
        }
        if (item.fieldType === 'date') {
          const element = document.querySelector(`.${item.id as string}-date`);
          if (element) {
            setClickableElements((prevState) => [...prevState, element]);
          }
        }
      });
    }, []);

    useOutsideClick(clickableElements, onSubmit);

    return (
      <div ref={ref} className={s.Row} onClick={onClickRow}>
        <Form<T> form={form} className={s.Form} onValuesChange={onFormChange}>
          {visibleColumns.map((item, index) => {
            const cell = cells[index];
            const editable = item.editable
              ? typeof item.editable === 'boolean'
                ? item.editable
                : item.editable(cell.row.original)
              : false;
            if (cell) {
              const cellProps = cell.getCellProps();
              if (item.fieldType === 'no-field' || !editable) {
                return (
                  <div {...cellProps} className={cellClassName} key={item.id}>
                    <div style={{ width: item.width, flex: `${item.width ?? 150} 0 auto` }} className={s.NotFieldCell}>
                      <CellAlign type={item.type}>
                        {formatCellByColumnType(cell.render(TableRenderComponents.Cell) as ReactElement, item)}
                      </CellAlign>
                    </div>
                  </div>
                );
              } else {
                const rules = getInputRulesFromColumnItem<T, TFilter, TSorting>(item);
                return (
                  <div {...cellProps} className={cellClassName} key={item.id}>
                    <Form.Item
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                      name={item.id as any}
                      style={{
                        width: item.width,
                        flex: `${item.width ?? 150} 0 auto`,
                        ...(item.formItemProps?.style || {}),
                      }}
                      className={classNames(s.Cell, item.formItemProps?.className)}
                      valuePropName={item.fieldType === 'checkbox' ? 'checked' : 'value'}
                      {...{ ...item.formItemProps, rules }}
                    >
                      {item.fieldType &&
                        data &&
                        React.createElement(
                          getCellField(item) as unknown as keyof ReactHTML,
                          getFieldProps(item, {
                            isCreate: false,
                            id: data.id,
                          }),
                        )}
                    </Form.Item>
                  </div>
                );
              }
            } else {
              return null;
            }
          })}
        </Form>
        <RowValidationPopover
          {...{ popoverData, errorRef }}
          onCancel={onHide}
          onContinue={() => setPopoverData(null)}
          cancelText={'Отменить'}
        />
      </div>
    );
  },
);
